import { Link } from "react-router-dom";

export function meta() {
  const description = "How RevHead Pro collects, stores, and uses your data. LeadHash LLC privacy notice.";
  return [
    { title: "Privacy Policy — RevHead Pro" },
    { name: "description", content: description },
    { property: "og:title", content: "Privacy Policy — RevHead Pro" },
    { property: "og:description", content: description },
    { property: "og:url", content: "https://revhead.pro/privacy" },
    { tagName: "link", rel: "canonical", href: "https://revhead.pro/privacy" },
  ];
}

export default function Privacy() {
  return (
    <>
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <h1
          className="mb-2 text-4xl font-bold text-[#E8EDF2]"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Privacy Policy
        </h1>

        <p className="mb-12 text-base text-[#7A8FA3]">
          Last updated: May 13, 2026
        </p>

        <div className="space-y-8 text-sm leading-relaxed text-[#7A8FA3]">
          {/* Intro */}
          <p>
            This Privacy Notice for LeadHash LLC (&quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;), describes how and why we might
            access, collect, store, use, and/or share (&quot;process&quot;) your
            personal information when you use our services
            (&quot;Services&quot;), including when you:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Visit our website at{" "}
              <a
                href="https://revhead.pro"
                className="text-[#4DB8C7] hover:underline"
              >
                https://revhead.pro
              </a>{" "}
              or any website of ours that links to this Privacy Notice
            </li>
            <li>
              Download and use our mobile application (RevHead Pro), or any
              other application of ours that links to this Privacy Notice
            </li>
            <li>
              Engage with us in other related ways, including any marketing or
              events
            </li>
          </ul>
          <p>
            <strong className="text-[#E8EDF2]">
              Questions or concerns?
            </strong>{" "}
            Reading this Privacy Notice will help you understand your privacy
            rights and choices. We are responsible for making decisions about how
            your personal information is processed. If you do not agree with our
            policies and practices, please do not use our Services.
          </p>

          {/* Summary of Key Points */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              Summary of Key Points
            </h2>
            <p className="italic text-[#9AABB8]">
              This summary provides key points from our Privacy Notice, but you
              can find out more details about any of these topics by clicking the
              link following each key point or by using our table of contents
              below to find the section you are looking for.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                What personal information do we process?
              </strong>{" "}
              When you visit, use, or navigate our Services, we may process
              personal information depending on how you interact with us and the
              Services, the choices you make, and the products and features you
              use.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                Do we process any sensitive personal information?
              </strong>{" "}
              We may collect approximate location data when you use
              certain features. Under some privacy laws, geolocation is
              classified as sensitive personal information. We only
              collect it with your permission and never in the background.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                Do we collect any information from third parties?
              </strong>{" "}
              We do not collect any information from third parties.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                How do we process your information?
              </strong>{" "}
              We process your information to provide, improve, and administer
              our Services, communicate with you, for security and fraud
              prevention, and to comply with law. We may also process your
              information for other purposes with your consent.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                In what situations and with which parties do we share personal
                information?
              </strong>{" "}
              We may share information in specific situations and with specific
              third parties.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                How do we keep your information safe?
              </strong>{" "}
              We have adequate organizational and technical processes and
              procedures in place to protect your personal information. However,
              no electronic transmission over the internet or information storage
              technology can be guaranteed to be 100% secure.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                What are your rights?
              </strong>{" "}
              Depending on where you are located geographically, the applicable
              privacy law may mean you have certain rights regarding your
              personal information.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                How do you exercise your rights?
              </strong>{" "}
              The easiest way to exercise your rights is by submitting a{" "}
              <a
                href="https://app.termly.io/dsar/0436acce-46f2-4600-bf65-a50ea852a0df"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                data subject access request
              </a>
              , or by contacting us. We will consider and act upon any request
              in accordance with applicable data protection laws.
            </p>
          </section>

          {/* Table of Contents */}
          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              Table of Contents
            </h2>
            <ol className="list-decimal space-y-1 pl-6">
              <li>
                <a href="#infocollect" className="text-[#4DB8C7] hover:underline">
                  What Information Do We Collect?
                </a>
              </li>
              <li>
                <a href="#infouse" className="text-[#4DB8C7] hover:underline">
                  How Do We Process Your Information?
                </a>
              </li>
              <li>
                <a href="#legalbases" className="text-[#4DB8C7] hover:underline">
                  What Legal Bases Do We Rely On to Process Your Personal
                  Information?
                </a>
              </li>
              <li>
                <a href="#whoshare" className="text-[#4DB8C7] hover:underline">
                  When and With Whom Do We Share Your Personal Information?
                </a>
              </li>
              <li>
                <a href="#cookies" className="text-[#4DB8C7] hover:underline">
                  Do We Use Cookies and Other Tracking Technologies?
                </a>
              </li>
              <li>
                <a href="#ai" className="text-[#4DB8C7] hover:underline">
                  Do We Offer Artificial Intelligence-Based Products?
                </a>
              </li>
              <li>
                <a href="#sociallogins" className="text-[#4DB8C7] hover:underline">
                  How Do We Handle Your Social Logins?
                </a>
              </li>
              <li>
                <a href="#inforetain" className="text-[#4DB8C7] hover:underline">
                  How Long Do We Keep Your Information?
                </a>
              </li>
              <li>
                <a href="#infosafe" className="text-[#4DB8C7] hover:underline">
                  How Do We Keep Your Information Safe?
                </a>
              </li>
              <li>
                <a href="#infominors" className="text-[#4DB8C7] hover:underline">
                  Do We Collect Information from Minors?
                </a>
              </li>
              <li>
                <a href="#privacyrights" className="text-[#4DB8C7] hover:underline">
                  What Are Your Privacy Rights?
                </a>
              </li>
              <li>
                <a href="#dnt" className="text-[#4DB8C7] hover:underline">
                  Controls for Do-Not-Track Features
                </a>
              </li>
              <li>
                <a href="#uslaws" className="text-[#4DB8C7] hover:underline">
                  Do United States Residents Have Specific Privacy Rights?
                </a>
              </li>
              <li>
                <a href="#policyupdates" className="text-[#4DB8C7] hover:underline">
                  Do We Make Updates to This Notice?
                </a>
              </li>
              <li>
                <a href="#contact" className="text-[#4DB8C7] hover:underline">
                  How Can You Contact Us About This Notice?
                </a>
              </li>
              <li>
                <a href="#request" className="text-[#4DB8C7] hover:underline">
                  How Can You Review, Update, or Delete the Data We Collect from
                  You?
                </a>
              </li>
            </ol>
          </section>

          {/* 1. What Information Do We Collect */}
          <section id="infocollect" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              1. What Information Do We Collect?
            </h2>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Personal information you disclose to us
            </h3>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We collect personal information that you provide to us.
              </em>
            </p>
            <p>
              We collect personal information that you voluntarily provide to us
              when you register on the Services, express an interest in
              obtaining information about us or our products and Services, when
              you participate in activities on the Services, or otherwise when
              you contact us.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                Personal Information Provided by You.
              </strong>{" "}
              The personal information that we collect depends on the context of
              your interactions with us and the Services, the choices you make,
              and the products and features you use. The personal information we
              collect may include the following:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Names</li>
              <li>Phone numbers</li>
              <li>Email addresses</li>
              <li>Mailing addresses</li>
              <li>Debit/credit card numbers</li>
              <li>Approximate location data (city-block level, when you choose to share it)</li>
            </ul>
            <p>
              <strong className="text-[#E8EDF2]">
                Sensitive Information.
              </strong>{" "}
              When you use the Spotted or Meets features, we may
              collect your approximate location (city-block level) if
              you grant permission. Under certain privacy laws (such as
              the CCPA/CPRA), geolocation data is considered sensitive
              personal information. We only collect location data when
              you actively use these features, never in the background.
              You can deny or revoke location permission at any time in
              your device settings without losing access to other app
              features.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">Payment Data.</strong> We may
              collect data necessary to process your payment if you choose to
              make purchases, such as your payment instrument number, and the
              security code associated with your payment instrument. All payment
              data is handled and stored by{" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Stripe
              </a>
              .
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                Social Media Login Data.
              </strong>{" "}
              We may provide you with the option to register with us using your
              existing social media account details, like your Facebook, X, or
              other social media account. If you choose to register in this way,
              we will collect certain profile information about you from the
              social media provider, as described in the section called{" "}
              <a
                href="#sociallogins"
                className="text-[#4DB8C7] hover:underline"
              >
                &quot;How Do We Handle Your Social Logins?&quot;
              </a>{" "}
              below.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">Application Data.</strong> If
              you use our application(s), we also may collect the following
              information if you choose to provide us with access or permission:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                <em>Push Notifications.</em> We may request to send you push
                notifications regarding your account or certain features of the
                application(s). If you wish to opt out from receiving these
                types of communications, you may turn them off in your
                device&apos;s settings.
              </li>
              <li>
                <em>Geolocation Data.</em> When you use the Spotted or
                Meets features and grant location permission, we collect
                your approximate device location to associate your posts
                with nearby car meets. Location is captured only at the
                moment you create a post — we do not track your location
                continuously or in the background. You can disable
                location sharing at any time via the toggle in the Spotted
                composer or through your device&apos;s settings. Location
                data attached to posts is stored as part of the post
                record and is subject to the same retention and deletion
                policies as other content you create. When you delete a
                post or your account, all associated location data is
                permanently removed.
              </li>
            </ul>
            <p>
              This information is primarily needed to maintain the security and
              operation of our application(s), for troubleshooting, and for our
              internal analytics and reporting purposes.
            </p>
            <p>
              All personal information that you provide to us must be true,
              complete, and accurate, and you must notify us of any changes to
              such personal information.
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Information automatically collected
            </h3>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                Some information — such as your Internet Protocol (IP) address
                and/or browser and device characteristics — is collected
                automatically when you visit our Services.
              </em>
            </p>
            <p>
              We automatically collect certain information when you visit, use,
              or navigate the Services. This information does not reveal your
              specific identity (like your name or contact information) but may
              include device and usage information, such as your IP address,
              browser and device characteristics, operating system, language
              preferences, referring URLs, device name, country, location,
              information about how and when you use our Services, and other
              technical information. This information is primarily needed to
              maintain the security and operation of our Services, and for our
              internal analytics and reporting purposes.
            </p>
            <p>
              Like many businesses, we also collect information through cookies
              and similar technologies. You can find out more about this in our
              Cookie Notice:{" "}
              <Link
                to="/cookies"
                className="text-[#4DB8C7] hover:underline"
              >
                https://revhead.pro/cookies
              </Link>
              .
            </p>
            <p>The information we collect includes:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <em>Log and Usage Data.</em> Log and usage data is
                service-related, diagnostic, usage, and performance information
                our servers automatically collect when you access or use our
                Services and which we record in log files. Depending on how you
                interact with us, this log data may include your IP address,
                device information, browser type, and settings and information
                about your activity in the Services (such as the date/time
                stamps associated with your usage, pages and files viewed,
                searches, and other actions you take such as which features you
                use), device event information (such as system activity, error
                reports (sometimes called &quot;crash dumps&quot;), and hardware
                settings).
              </li>
              <li>
                <em>Device Data.</em> We collect device data such as information
                about your computer, phone, tablet, or other device you use to
                access the Services. Depending on the device used, this device
                data may include information such as your IP address (or proxy
                server), device and application identification numbers,
                location, browser type, hardware model, Internet service
                provider and/or mobile carrier, operating system, and system
                configuration information.
              </li>
            </ul>

            <h3 className="text-lg font-medium text-[#E8EDF2]">Google API</h3>
            <p>
              Our use of information received from Google APIs will adhere to{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Google API Services User Data Policy
              </a>
              , including the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Limited Use requirements
              </a>
              .
            </p>
          </section>

          {/* 2. How Do We Process Your Information */}
          <section id="infouse" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              2. How Do We Process Your Information?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We process your information to provide, improve, and administer
                our Services, communicate with you, for security and fraud
                prevention, and to comply with law. We may also process your
                information for other purposes only with your prior explicit
                consent.
              </em>
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                We process your personal information for a variety of reasons,
                depending on how you interact with our Services, including:
              </strong>
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-[#E8EDF2]">
                  To facilitate account creation and authentication and
                  otherwise manage user accounts.
                </strong>{" "}
                We may process your information so you can create and log in to
                your account, as well as keep your account in working order.
              </li>
              <li>
                <strong className="text-[#E8EDF2]">
                  To save or protect an individual&apos;s vital interest.
                </strong>{" "}
                We may process your information when necessary to save or
                protect an individual&apos;s vital interest, such as to prevent
                harm.
              </li>
            </ul>
          </section>

          {/* 3. Legal Bases */}
          <section id="legalbases" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              3. What Legal Bases Do We Rely On to Process Your Personal
              Information?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We only process your personal information when we believe it is
                necessary and we have a valid legal reason (i.e., legal basis)
                to do so under applicable law, like with your consent, to comply
                with laws, to provide you with services to enter into or fulfill
                our contractual obligations, to protect your rights, or to
                fulfill our legitimate business interests.
              </em>
            </p>

            <p className="italic text-[#9AABB8]">
              <strong>
                <u>
                  If you are located in the EU or UK, this section applies to
                  you.
                </u>
              </strong>
            </p>
            <p>
              The General Data Protection Regulation (GDPR) and UK GDPR require
              us to explain the valid legal bases we rely on in order to process
              your personal information. As such, we may rely on the following
              legal bases to process your personal information:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-[#E8EDF2]">Consent.</strong> We may
                process your information if you have given us permission (i.e.,
                consent) to use your personal information for a specific
                purpose. You can withdraw your consent at any time.
              </li>
              <li>
                <strong className="text-[#E8EDF2]">Legal Obligations.</strong>{" "}
                We may process your information where we believe it is necessary
                for compliance with our legal obligations, such as to cooperate
                with a law enforcement body or regulatory agency, exercise or
                defend our legal rights, or disclose your information as
                evidence in litigation in which we are involved.
              </li>
              <li>
                <strong className="text-[#E8EDF2]">Vital Interests.</strong> We
                may process your information where we believe it is necessary to
                protect your vital interests or the vital interests of a third
                party, such as situations involving potential threats to the
                safety of any person.
              </li>
            </ul>

            <p className="italic text-[#9AABB8]">
              <strong>
                <u>
                  If you are located in Canada, this section applies to you.
                </u>
              </strong>
            </p>
            <p>
              We may process your information if you have given us specific
              permission (i.e., express consent) to use your personal
              information for a specific purpose, or in situations where your
              permission can be inferred (i.e., implied consent). You can{" "}
              <a
                href="#withdrawconsent"
                className="text-[#4DB8C7] hover:underline"
              >
                withdraw your consent
              </a>{" "}
              at any time.
            </p>
            <p>
              In some exceptional cases, we may be legally permitted under
              applicable law to process your information without your consent,
              including, for example:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                If collection is clearly in the interests of an individual and
                consent cannot be obtained in a timely way
              </li>
              <li>For investigations and fraud detection and prevention</li>
              <li>
                For business transactions provided certain conditions are met
              </li>
              <li>
                If it is contained in a witness statement and the collection is
                necessary to assess, process, or settle an insurance claim
              </li>
              <li>
                For identifying injured, ill, or deceased persons and
                communicating with next of kin
              </li>
              <li>
                If we have reasonable grounds to believe an individual has been,
                is, or may be victim of financial abuse
              </li>
              <li>
                If it is reasonable to expect collection and use with consent
                would compromise the availability or the accuracy of the
                information and the collection is reasonable for purposes
                related to investigating a breach of an agreement or a
                contravention of the laws of Canada or a province
              </li>
              <li>
                If disclosure is required to comply with a subpoena, warrant,
                court order, or rules of the court relating to the production of
                records
              </li>
              <li>
                If it was produced by an individual in the course of their
                employment, business, or profession and the collection is
                consistent with the purposes for which the information was
                produced
              </li>
              <li>
                If the collection is solely for journalistic, artistic, or
                literary purposes
              </li>
              <li>
                If the information is publicly available and is specified by the
                regulations
              </li>
              <li>
                We may disclose de-identified information for approved research
                or statistics projects, subject to ethics oversight and
                confidentiality commitments
              </li>
            </ul>
          </section>

          {/* 4. When and With Whom Do We Share */}
          <section id="whoshare" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              4. When and With Whom Do We Share Your Personal Information?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We may share information in specific situations described in
                this section and/or with the following third parties.
              </em>
            </p>
            <p>
              We may need to share your personal information in the following
              situations:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-[#E8EDF2]">
                  Business Transfers.
                </strong>{" "}
                We may share or transfer your information in connection with, or
                during negotiations of, any merger, sale of company assets,
                financing, or acquisition of all or a portion of our business to
                another company.
              </li>
            </ul>
          </section>

          {/* 5. Cookies */}
          <section id="cookies" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              5. Do We Use Cookies and Other Tracking Technologies?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We may use cookies and other tracking technologies to collect
                and store your information.
              </em>
            </p>
            <p>
              We may use cookies and similar tracking technologies (like web
              beacons and pixels) to gather information when you interact with
              our Services. Some online tracking technologies help us maintain
              the security of our Services and your account, prevent crashes,
              fix bugs, save your preferences, and assist with basic site
              functions.
            </p>
            <p>
              We also permit third parties and service providers to use online
              tracking technologies on our Services for analytics and
              advertising, including to help manage and display advertisements,
              to tailor advertisements to your interests, or to send abandoned
              shopping cart reminders (depending on your communication
              preferences). The third parties and service providers use their
              technology to provide advertising about products and services
              tailored to your interests which may appear either on our Services
              or on other websites.
            </p>
            <p>
              To the extent these online tracking technologies are deemed to be
              a &quot;sale&quot;/&quot;sharing&quot; (which includes targeted
              advertising, as defined under the applicable laws) under
              applicable US state laws, you can opt out of these online tracking
              technologies by submitting a request as described below under
              section{" "}
              <a href="#uslaws" className="text-[#4DB8C7] hover:underline">
                &quot;Do United States Residents Have Specific Privacy
                Rights?&quot;
              </a>
            </p>
            <p>
              Specific information about how we use such technologies and how
              you can refuse certain cookies is set out in our Cookie Notice:{" "}
              <Link
                to="/cookies"
                className="text-[#4DB8C7] hover:underline"
              >
                https://revhead.pro/cookies
              </Link>
              .
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Meta (Facebook) SDK
            </h3>
            <p>
              Our mobile application includes the Meta (Facebook) Software
              Development Kit (SDK) to measure the performance of advertising
              campaigns we run on Meta&apos;s platforms (Facebook, Instagram).
              The SDK may collect your device&apos;s advertising identifier
              (IDFA on iOS, Advertising ID on Android), app install and launch
              events, and significant conversion events such as account
              registration and subscription purchases. Meta uses this data on
              our behalf for ad attribution, ad delivery optimization, and
              aggregated reporting under{" "}
              <a
                href="https://www.facebook.com/business/help/471978536642445"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Apple&apos;s SKAdNetwork and Aggregated Event Measurement
              </a>
              . On iOS 14.5 and later, you will be asked to grant or deny app
              tracking permission via the system prompt the first time you
              open the app — denying tracking limits the advertising data the
              SDK can share with Meta. On Android, you can reset or limit your
              Advertising ID at any time in your device&apos;s Google settings.
              For more information on Meta&apos;s privacy practices, please see{" "}
              <a
                href="https://www.facebook.com/privacy/policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Meta&apos;s Privacy Policy
              </a>
              .
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Google Analytics
            </h3>
            <p>
              We may share your information with Google Analytics to track and
              analyze the use of the Services. The Google Analytics Advertising
              Features that we may use include: Google Analytics Demographics
              and Interests Reporting. To opt out of being tracked by Google
              Analytics across the Services, visit{" "}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
              . You can opt out of Google Analytics Advertising Features through{" "}
              <a
                href="https://adssettings.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Ads Settings
              </a>{" "}
              and Ad Settings for mobile apps. Other opt out means include{" "}
              <a
                href="http://optout.networkadvertising.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                http://optout.networkadvertising.org/
              </a>{" "}
              and{" "}
              <a
                href="http://www.networkadvertising.org/mobile-choice"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                http://www.networkadvertising.org/mobile-choice
              </a>
              . For more information on the privacy practices of Google, please
              visit the{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Google Privacy &amp; Terms page
              </a>
              .
            </p>
          </section>

          {/* 6. AI Products */}
          <section id="ai" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              6. Do We Offer Artificial Intelligence-Based Products?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We offer products, features, or tools powered by artificial
                intelligence, machine learning, or similar technologies.
              </em>
            </p>
            <p>
              As part of our Services, we offer products, features, or tools
              powered by artificial intelligence, machine learning, or similar
              technologies (collectively, &quot;AI Products&quot;). These tools
              are designed to enhance your experience and provide you with
              innovative solutions. The terms in this Privacy Notice govern your
              use of the AI Products within our Services.
            </p>
            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Use of AI Technologies
            </h3>
            <p>
              We provide the AI Products through third-party service providers
              (&quot;AI Service Providers&quot;), including Google Cloud AI. As
              outlined in this Privacy Notice, your input, output, and personal
              information will be shared with and processed by these AI Service
              Providers to enable your use of our AI Products for purposes
              outlined in{" "}
              <a
                href="#legalbases"
                className="text-[#4DB8C7] hover:underline"
              >
                &quot;What Legal Bases Do We Rely On to Process Your Personal
                Information?&quot;
              </a>{" "}
              You must not use the AI Products in any way that violates the
              terms or policies of any AI Service Provider.
            </p>
            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Our AI Products
            </h3>
            <p>
              Our AI Products are designed for the following functions:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Text analysis</li>
              <li>Natural language processing</li>
            </ul>
            <h3 className="text-lg font-medium text-[#E8EDF2]">
              How We Process Your Data Using AI
            </h3>
            <p>
              All personal information processed using our AI Products is
              handled in line with our Privacy Notice and our agreement with
              third parties. This ensures high security and safeguards your
              personal information throughout the process, giving you peace of
              mind about your data&apos;s safety.
            </p>
          </section>

          {/* 7. Social Logins */}
          <section id="sociallogins" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              7. How Do We Handle Your Social Logins?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                If you choose to register or log in to our Services using a
                social media account, we may have access to certain information
                about you.
              </em>
            </p>
            <p>
              Our Services offer you the ability to register and log in using
              your third-party social media account details (like your Facebook
              or X logins). Where you choose to do this, we will receive certain
              profile information about you from your social media provider. The
              profile information we receive may vary depending on the social
              media provider concerned, but will often include your name, email
              address, friends list, and profile picture, as well as other
              information you choose to make public on such a social media
              platform.
            </p>
            <p>
              We will use the information we receive only for the purposes that
              are described in this Privacy Notice or that are otherwise made
              clear to you on the relevant Services. Please note that we do not
              control, and are not responsible for, other uses of your personal
              information by your third-party social media provider. We
              recommend that you review their privacy notice to understand how
              they collect, use, and share your personal information, and how
              you can set your privacy preferences on their sites and apps.
            </p>
          </section>

          {/* 8. Data Retention */}
          <section id="inforetain" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              8. How Long Do We Keep Your Information?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this Privacy Notice unless otherwise
                required by law.
              </em>
            </p>
            <p>
              We will only keep your personal information for as long as it is
              necessary for the purposes set out in this Privacy Notice, unless
              a longer retention period is required or permitted by law (such as
              tax, accounting, or other legal requirements). No purpose in this
              notice will require us keeping your personal information for
              longer than the period of time in which users have an account with
              us.
            </p>
            <p>
              When we have no ongoing legitimate business need to process your
              personal information, we will either delete or anonymize such
              information, or, if this is not possible (for example, because
              your personal information has been stored in backup archives),
              then we will securely store your personal information and isolate
              it from any further processing until deletion is possible.
            </p>
          </section>

          {/* 9. Information Security */}
          <section id="infosafe" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              9. How Do We Keep Your Information Safe?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We aim to protect your personal information through a system of
                organizational and technical security measures.
              </em>
            </p>
            <p>
              We have implemented appropriate and reasonable technical and
              organizational security measures designed to protect the security
              of any personal information we process. However, despite our
              safeguards and efforts to secure your information, no electronic
              transmission over the Internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorized
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
              Although we will do our best to protect your personal information,
              transmission of personal information to and from our Services is
              at your own risk. You should only access the Services within a
              secure environment.
            </p>
          </section>

          {/* 10. Minors */}
          <section id="infominors" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              10. Do We Collect Information from Minors?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                We do not knowingly collect data from or market to children
                under 18 years of age or the equivalent age as specified by law
                in your jurisdiction.
              </em>
            </p>
            <p>
              We do not knowingly collect, solicit data from, or market to
              children under 18 years of age or the equivalent age as specified
              by law in your jurisdiction, nor do we knowingly sell such
              personal information. By using the Services, you represent that
              you are at least 18 or the equivalent age as specified by law in
              your jurisdiction or that you are the parent or guardian of such a
              minor and consent to such minor dependent&apos;s use of the
              Services. If we learn that personal information from users less
              than 18 years of age or the equivalent age as specified by law in
              your jurisdiction has been collected, we will deactivate the
              account and take reasonable measures to promptly delete such data
              from our records. If you become aware of any data we may have
              collected from children under age 18 or the equivalent age as
              specified by law in your jurisdiction, please contact us at{" "}
              <a
                href="mailto:gethelp@revhead.pro"
                className="text-[#4DB8C7] hover:underline"
              >
                gethelp@revhead.pro
              </a>
              .
            </p>
          </section>

          {/* 11. Privacy Rights */}
          <section id="privacyrights" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              11. What Are Your Privacy Rights?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                Depending on your state of residence in the US or in some
                regions, such as the European Economic Area (EEA), United
                Kingdom (UK), Switzerland, and Canada, you have rights that
                allow you greater access to and control over your personal
                information. You may review, change, or terminate your account
                at any time, depending on your country, province, or state of
                residence.
              </em>
            </p>
            <p>
              In some regions (like the EEA, UK, Switzerland, and Canada), you
              have certain rights under applicable data protection laws. These
              may include the right (i) to request access and obtain a copy of
              your personal information, (ii) to request rectification or
              erasure; (iii) to restrict the processing of your personal
              information; (iv) if applicable, to data portability; and (v) not
              to be subject to automated decision-making. If a decision that
              produces legal or similarly significant effects is made solely by
              automated means, we will inform you, explain the main factors, and
              offer a simple way to request human review. In certain
              circumstances, you may also have the right to object to the
              processing of your personal information. You can make such a
              request by contacting us by using the contact details provided in
              the section{" "}
              <a href="#contact" className="text-[#4DB8C7] hover:underline">
                &quot;How Can You Contact Us About This Notice?&quot;
              </a>{" "}
              below.
            </p>
            <p>
              We will consider and act upon any request in accordance with
              applicable data protection laws.
            </p>
            <p>
              If you are located in the EEA or UK and you believe we are
              unlawfully processing your personal information, you also have the
              right to complain to your{" "}
              <a
                href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Member State data protection authority
              </a>{" "}
              or{" "}
              <a
                href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                UK data protection authority
              </a>
              .
            </p>
            <p>
              If you are located in Switzerland, you may contact the{" "}
              <a
                href="https://www.edoeb.admin.ch/edoeb/en/home.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                Federal Data Protection and Information Commissioner
              </a>
              .
            </p>

            <p id="withdrawconsent">
              <strong className="text-[#E8EDF2]">
                <u>Withdrawing your consent:</u>
              </strong>{" "}
              If we are relying on your consent to process your personal
              information, which may be express and/or implied consent depending
              on the applicable law, you have the right to withdraw your consent
              at any time. You can withdraw your consent at any time by
              contacting us by using the contact details provided in the section{" "}
              <a href="#contact" className="text-[#4DB8C7] hover:underline">
                &quot;How Can You Contact Us About This Notice?&quot;
              </a>{" "}
              below.
            </p>
            <p>
              However, please note that this will not affect the lawfulness of
              the processing before its withdrawal nor, when applicable law
              allows, will it affect the processing of your personal information
              conducted in reliance on lawful processing grounds other than
              consent.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                <u>
                  Opting out of marketing and promotional communications:
                </u>
              </strong>{" "}
              You can unsubscribe from our marketing and promotional
              communications at any time by clicking on the unsubscribe link in
              the emails that we send, replying &quot;STOP&quot; or
              &quot;UNSUBSCRIBE&quot; to the SMS messages that we send, or by
              contacting us using the details provided in the section{" "}
              <a href="#contact" className="text-[#4DB8C7] hover:underline">
                &quot;How Can You Contact Us About This Notice?&quot;
              </a>{" "}
              below. You will then be removed from the marketing lists. However,
              we may still communicate with you — for example, to send you
              service-related messages that are necessary for the administration
              and use of your account, to respond to service requests, or for
              other non-marketing purposes.
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Account Information
            </h3>
            <p>
              If you would at any time like to review or change the information
              in your account or terminate your account, you can:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                Log in to your account settings and update your user account.
              </li>
            </ul>
            <p>
              Upon your request to terminate your account, we will deactivate or
              delete your account and information from our active databases.
              However, we may retain some information in our files to prevent
              fraud, troubleshoot problems, assist with any investigations,
              enforce our legal terms and/or comply with applicable legal
              requirements.
            </p>
            <p>
              <strong className="text-[#E8EDF2]">
                <u>Cookies and similar technologies:</u>
              </strong>{" "}
              Most Web browsers are set to accept cookies by default. If you
              prefer, you can usually choose to set your browser to remove
              cookies and to reject cookies. If you choose to remove cookies or
              reject cookies, this could affect certain features or services of
              our Services. For further information, please see our Cookie
              Notice:{" "}
              <Link
                to="/cookies"
                className="text-[#4DB8C7] hover:underline"
              >
                https://revhead.pro/cookies
              </Link>
              .
            </p>
          </section>

          {/* 12. DNT */}
          <section id="dnt" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              12. Controls for Do-Not-Track Features
            </h2>
            <p>
              Most web browsers and some mobile operating systems and mobile
              applications include a Do-Not-Track (&quot;DNT&quot;) feature or
              setting you can activate to signal your privacy preference not to
              have data about your online browsing activities monitored and
              collected. At this stage, no uniform technology standard for
              recognizing and implementing DNT signals has been finalized. As
              such, we do not currently respond to DNT browser signals or any
              other mechanism that automatically communicates your choice not to
              be tracked online. If a standard for online tracking is adopted
              that we must follow in the future, we will inform you about that
              practice in a revised version of this Privacy Notice.
            </p>
            <p>
              California law requires us to let you know how we respond to web
              browser DNT signals. Because there currently is not an industry or
              legal standard for recognizing or honoring DNT signals, we do not
              respond to them at this time.
            </p>
          </section>

          {/* 13. US Residents */}
          <section id="uslaws" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              13. Do United States Residents Have Specific Privacy Rights?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                If you are a resident of California, Colorado, Connecticut,
                Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota,
                Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode
                Island, Tennessee, Texas, Utah, or Virginia, you may have the
                right to request access to and receive details about the
                personal information we maintain about you and how we have
                processed it, correct inaccuracies, get a copy of, or delete
                your personal information. You may also have the right to
                withdraw your consent to our processing of your personal
                information. These rights may be limited in some circumstances
                by applicable law.
              </em>
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Your Rights
            </h3>
            <p>
              You have rights under certain US state data protection laws.
              However, these rights are not absolute, and in certain cases, we
              may decline your request as permitted by law. These rights
              include:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                <strong className="text-[#E8EDF2]">Right to know</strong>{" "}
                whether or not we are processing your personal data
              </li>
              <li>
                <strong className="text-[#E8EDF2]">Right to access</strong>{" "}
                your personal data
              </li>
              <li>
                <strong className="text-[#E8EDF2]">Right to correct</strong>{" "}
                inaccuracies in your personal data
              </li>
              <li>
                <strong className="text-[#E8EDF2]">Right to request</strong>{" "}
                the deletion of your personal data
              </li>
              <li>
                <strong className="text-[#E8EDF2]">
                  Right to obtain a copy
                </strong>{" "}
                of the personal data you previously shared with us
              </li>
              <li>
                <strong className="text-[#E8EDF2]">
                  Right to non-discrimination
                </strong>{" "}
                for exercising your rights
              </li>
              <li>
                <strong className="text-[#E8EDF2]">Right to opt out</strong> of
                the processing of your personal data if it is used for targeted
                advertising (or sharing as defined under California&apos;s
                privacy law), the sale of personal data, or profiling in
                furtherance of decisions that produce legal or similarly
                significant effects (&quot;profiling&quot;)
              </li>
              <li>
                <strong className="text-[#E8EDF2]">
                  Right to limit use of sensitive personal information
                </strong>{" "}
                — Under California law (CCPA/CPRA), you may request that
                we limit the use of your sensitive personal information
                (such as approximate geolocation) to purposes necessary to
                provide our Services. We currently use location data solely
                for in-app functionality (matching spotted posts to nearby
                car meets) and do not use it for profiling or advertising.
              </li>
            </ul>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              How to Exercise Your Rights
            </h3>
            <p>
              To exercise these rights, you can contact us by submitting a{" "}
              <a
                href="https://app.termly.io/dsar/0436acce-46f2-4600-bf65-a50ea852a0df"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                data subject access request
              </a>
              , by emailing us at{" "}
              <a
                href="mailto:gethelp@revhead.pro"
                className="text-[#4DB8C7] hover:underline"
              >
                gethelp@revhead.pro
              </a>
              , or by referring to the contact details at the bottom of this
              document.
            </p>
            <p>
              Under certain US state data protection laws, you can designate an
              authorized agent to make a request on your behalf. We may deny a
              request from an authorized agent that does not submit proof that
              they have been validly authorized to act on your behalf in
              accordance with applicable laws.
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              Request Verification
            </h3>
            <p>
              Upon receiving your request, we will need to verify your identity
              to determine you are the same person about whom we have the
              information in our system. We will only use personal information
              provided in your request to verify your identity or authority to
              make the request. However, if we cannot verify your identity from
              the information already maintained by us, we may request that you
              provide additional information for the purposes of verifying your
              identity and for security or fraud-prevention purposes.
            </p>
            <p>
              If you submit the request through an authorized agent, we may need
              to collect additional information to verify your identity before
              processing your request and the agent will need to provide a
              written and signed permission from you to submit such request on
              your behalf.
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">Appeals</h3>
            <p>
              Under certain US state data protection laws, if we decline to take
              action regarding your request, you may appeal our decision by
              emailing us at{" "}
              <a
                href="mailto:gethelp@revhead.pro"
                className="text-[#4DB8C7] hover:underline"
              >
                gethelp@revhead.pro
              </a>
              . We will inform you in writing of any action taken or not taken
              in response to the appeal, including a written explanation of the
              reasons for the decisions. If your appeal is denied, you may
              submit a complaint to your state attorney general.
            </p>

            <h3 className="text-lg font-medium text-[#E8EDF2]">
              California &quot;Shine The Light&quot; Law
            </h3>
            <p>
              California Civil Code Section 1798.83, also known as the
              &quot;Shine The Light&quot; law, permits our users who are
              California residents to request and obtain from us, once a year
              and free of charge, information about categories of personal
              information (if any) we disclosed to third parties for direct
              marketing purposes and the names and addresses of all third
              parties with which we shared personal information in the
              immediately preceding calendar year. If you are a California
              resident and would like to make such a request, please submit your
              request in writing to us by using the contact details provided in
              the section{" "}
              <a href="#contact" className="text-[#4DB8C7] hover:underline">
                &quot;How Can You Contact Us About This Notice?&quot;
              </a>
            </p>
          </section>

          {/* 14. Updates */}
          <section id="policyupdates" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              14. Do We Make Updates to This Notice?
            </h2>
            <p>
              <strong className="text-[#E8EDF2]">In Short:</strong>{" "}
              <em>
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws.
              </em>
            </p>
            <p>
              We may update this Privacy Notice from time to time. The updated
              version will be indicated by an updated &quot;Revised&quot; date
              at the top of this Privacy Notice. If we make material changes to
              this Privacy Notice, we may notify you either by prominently
              posting a notice of such changes or by directly sending you a
              notification. We encourage you to review this Privacy Notice
              frequently to be informed of how we are protecting your
              information.
            </p>
          </section>

          {/* 15. Contact */}
          <section id="contact" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              15. How Can You Contact Us About This Notice?
            </h2>
            <p>
              If you have questions or comments about this notice, you may email
              us at{" "}
              <a
                href="mailto:gethelp@revhead.pro"
                className="text-[#4DB8C7] hover:underline"
              >
                gethelp@revhead.pro
              </a>{" "}
              or contact us by post at:
            </p>
            <address className="not-italic text-[#9AABB8]">
              LeadHash LLC
              <br />
              7918 Jones Branch Dr
              <br />
              McLean, VA 22102
              <br />
              United States
            </address>
          </section>

          {/* 16. Data Review */}
          <section id="request" className="space-y-4">
            <h2 className="text-xl font-semibold text-[#E8EDF2]">
              16. How Can You Review, Update, or Delete the Data We Collect from
              You?
            </h2>
            <p>
              Based on the applicable laws of your country or state of residence
              in the US, you may have the right to request access to the
              personal information we collect from you, details about how we
              have processed it, correct inaccuracies, or delete your personal
              information. You may also have the right to withdraw your consent
              to our processing of your personal information. These rights may
              be limited in some circumstances by applicable law. To request to
              review, update, or delete your personal information, please fill
              out and submit a{" "}
              <a
                href="https://app.termly.io/dsar/0436acce-46f2-4600-bf65-a50ea852a0df"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4DB8C7] hover:underline"
              >
                data subject access request
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
